<template>
    <div>
        <draggable-accordion
            class="mb-2"
            v-for="content in props.data.contents"
            :unique_id="content.unique_id"
            :edit="content.edit"
            @remove="removeContent"
            @toggle="toggleContent(content)"
        >
            <template #title>{{ contentName }}</template>
            <template #content>
                <div class="grid lg:grid-cols-2 gap-6 gap-y-4">

                    <div class="col-span-2">
                        <div class="flex items-center justify-between mb-1.5">
                            <label class="text-sm">Image</label>
                            <div v-if="content.files.content_block_image?.url" class="flex items-center gap-3">
                                <div class="flex items-center">
                                    <span class="text-xs text-gray-400 mr-1">Position:</span>
                                    <select
                                        :value="content.image_position || 'top'"
                                        @change="content.image_position = $event.target.value"
                                        class="border-0 focus:ring-0 leading-4 text-xs text-gray-500 p-0 pr-6 bg-transparent cursor-pointer"
                                    >
                                        <option value="top">Top</option>
                                        <option value="left">Left</option>
                                        <option value="right">Right</option>
                                    </select>
                                </div>
                                <div v-if="content.image_position === 'left' || content.image_position === 'right'" class="flex items-center">
                                    <span class="text-xs text-gray-400 mr-1">Width:</span>
                                    <select
                                        :value="content.image_width || '1/2'"
                                        @change="content.image_width = $event.target.value"
                                        class="border-0 focus:ring-0 leading-4 text-xs text-gray-500 p-0 pr-6 bg-transparent cursor-pointer"
                                    >
                                        <option value="1/2">1/2</option>
                                        <option value="2/5">2/5</option>
                                        <option value="1/3">1/3</option>
                                    </select>
                                </div>
                                <div class="flex items-center">
                                    <span class="text-xs text-gray-400 mr-1">Border:</span>
                                    <select
                                        :value="(content.image_border ?? true) ? 'show' : 'hide'"
                                        @change="content.image_border = $event.target.value === 'show'"
                                        class="border-0 focus:ring-0 leading-4 text-xs text-gray-500 p-0 pr-6 bg-transparent cursor-pointer"
                                    >
                                        <option value="show">Show</option>
                                        <option value="hide">Hide</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <ResourceFileUpload
                            :unique_id="content.unique_id"
                            :data="content.files.content_block_image"
                            accept="image/*"
                            title="Click to upload or drag & drop"
                            name="content_block_image"
                            size="dropzone"
                            no_src="/images/upload-image.png"
                            @uploaded="(data) => content.files.content_block_image = data"
                            @deleted="(data) => content.files.content_block_image = data"
                        ></ResourceFileUpload>
                    </div>


                    <div class="col-span-2">
                        <text-field
                            label="Title"
                            v-model="content.title"
                            placeholder="Enter a title or leave it empty"
                        >
                        </text-field>
                    </div>

                    <div class="col-span-2">
                        <text-editor
                            :text="content.text"
                            @input="(newValue) => { content.text = newValue }"
                        />
                    </div>

                    <text-field
                        label="Button"
                        v-model="content.button"
                        placeholder="Enter button text"
                    >
                    </text-field>

                    <text-field
                        label="URL"
                        v-model="content.url"
                        placeholder="https://"
                        :prefixes="['https://', 'http://']"
                    >
                    </text-field>

                </div>
            </template>
        </draggable-accordion>

        <div class="flex justify-between items-center">
            <btn type="secondary" size="small" icon-name="add-circle" @click="addContent">Add {{ contentName }}</btn>
            <div class="flex items-center gap-2">
                <div v-if="data.display_blocks_in_columns" class="flex items-center">
                    <span class="text-sm text-gray-400 mr-1">Columns:</span>
                    <select
                        :value="data.columns_count || 3"
                        @change="data.columns_count = Number($event.target.value)"
                        class="border-0 focus:ring-0 leading-4 text-sm text-gray-500 p-0 pr-8 bg-transparent cursor-pointer"
                    >
                        <option :value="2">2</option>
                        <option :value="3">3</option>
                    </select>
                </div>

                <div class="flex justify-between items-center">
                    <toggle
                        name="columns_toggle"
                        size="medium"
                        v-model="data.display_blocks_in_columns"
                    />
                    <span class="text-sm font-medium">Display in columns</span>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import {defineProps} from 'vue';
import {uniqueId} from '../helpers';
import ResourceFileUpload from "./ResourceFileUpload.vue";
import DraggableAccordion from "../../../../elements/accordions/DraggableAccordion.vue";


const contentName = 'Content section';

const props = defineProps({
    data: Object
});

props.data.display_blocks_in_columns = props.data?.display_blocks_in_columns ?? false;
if (!props.data.columns_count) props.data.columns_count = 3;
props.data.contents = props.data?.contents ?? [];

// Initialize image_size for existing content with backwards compatibility
// Also ensure only one content is open at initialization
let foundFirstOpen = false;
props.data.contents.forEach(content => {
    if (!content.image_size) {
        content.image_size = content.retina_friendly_image ? '@2x' : 'medium';
    }
    if (content.edit) {
        if (foundFirstOpen) {
            content.edit = false;
        } else {
            foundFirstOpen = true;
        }
    }
});

const toggleContent = (element) => {
    const isOpening = !element.edit;
    if (isOpening) {
        props.data.contents.forEach(content => {
            if (content.unique_id !== element.unique_id) {
                content.edit = false;
            }
        });
    }
    element.edit = !element.edit;
}

const addContent = () => {
    // Close all existing content
    props.data.contents.forEach(content => {
        content.edit = false;
    });

    props.data.contents.push({
        unique_id: uniqueId(),
        edit: true,
        files: {
            content_block_image: {}
        },
        title: '',
        text: '',
        button: '',
        url: '',
        image_position: 'top',
        image_width: '1/2',
        image_border: true,
    });
}

const removeContent = (uniqueId) => {
    props.data.contents.splice(
        props.data.contents.findIndex(content => content.unique_id === uniqueId)
    , 1);
}
</script>
